import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ClientService } from '../../../services/client.service';
import {
    BaseComponent,
    CustomDimensionDialogData,
    CustomDimensionDialogSubmitData,
    CustomDimensionsComponent,
    CustomMetricsComponent,
    FeatureFlagService,
    Page,
    PusherService,
    UserService,
} from '@rubii/frontend-common';
import { CustomDimension, CustomMetric, CustomMetricStatus } from '@rubii/sdk-web-custom-metric/src/types';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WsEvent } from '@rubii/frontend-common/lib/models/events/ws-event.class';
import { Dimension, Metric, ReportType } from '@rubii/sdk-cdp-report/src/types';
import { DataType, GetDataRequest } from '@rubii/sdk-web-dashboard/src/types/widget';
import { RubiiFilter, RubiiReportOptions } from '@rubii/sdk-cdp-report/src/types/rubii';
import { customDimensionEvents, featureCustomDimension } from 'src/app/constants/features';

@Component({
    selector: 'app-custom-dimension-list',
    templateUrl: './custom-dimension-list.component.html',
    styleUrls: ['./custom-dimension-list.component.scss'],
})
export class CustomDimensionListComponent extends BaseComponent implements OnInit {
    private allCustomDimensions: CustomDimension[] = [];

    customDimensions: CustomDimension[] = [];
    dimensions: Dimension[] = [];
    timezone = '';
    keyword = '';

    format: string = '';

    CustomDimensionStatus = CustomMetricStatus;

    constructor(private client: ClientService, public user: UserService, private dialog: MatDialog, private snack: MatSnackBar, private pusher: PusherService, private feature: FeatureFlagService) {
        super();
        this.page = new Page(1, 15, 0);
        this.format = this.user.user.tenant.dateFormat.replace('YYYY', 'yyyy').replace('DD', 'dd') + ' HH:mm:ss OOOO';
        this.timezone = this.user.user.tenant.timezone;
        this.pusher.bind('custom-dimension-list', 'custom-dimension-updated', this.customDimensionUpdated.bind(this));
    }

    async ngOnInit() {
        this.init();

        const request: GetDataRequest = {
            dataType: DataType.Table,
            options: {
                reportType: ReportType.Rubii,
                filters: {} as RubiiFilter,
                dimensionIds: [],
                metricIds: [],
            },
        };
        this.dimensions = await this.client.widget.dimensions(request);
    }

    async init() {
        this.page.pageId = 1;
        await this.customDimensionUpdated(null);
    }

    async customDimensionUpdated(event: WsEvent) {
        this.allCustomDimensions = (await this.client.customDimension.list()).sort((a, b) => new Date(b.updatedate).getTime() - new Date(a.updatedate).getTime());
        this.paginate();
    }

    paginate() {
        const filteredCMs = this.allCustomDimensions.filter((cm) => !this.keyword || (cm.name + ' ' + cm.displayName).toLowerCase().includes(this.keyword.toLowerCase()));
        this.page.total = filteredCMs.length;
        if (filteredCMs.length === 0) {
            this.page.pageId = 1;
            this.customDimensions = [];
        } else {
            this.page.pageId = Math.min(this.page.pageId, Math.ceil(filteredCMs.length / this.page.pageSize));
            this.customDimensions = filteredCMs.slice((this.page.pageId - 1) * this.page.pageSize, this.page.pageId * this.page.pageSize);
        }
    }

    async archive(id: number) {
        try {
            await this.client.customDimension.archive(id);
            this.allCustomDimensions.find((cm) => cm._id === id).status = this.CustomDimensionStatus.Archived;
            this.paginate();
            this.sendAnalytics('Archive');
        } catch (error) {
            this.openSnackBarWarning(this.snack, error.message, 'close');
        }
    }

    pageChanged(event: PageEvent) {
        this.page.pageId = event.pageIndex + 1;
        this.page.pageSize = event.pageSize;
        this.paginate();
    }

    clone(id: number) {
        const dialogData: CustomDimensionDialogData = {
            dimensionsData: this.dimensions,
            cloneItemId: id,
        };
        this.openPopup()(dialogData);
    }

    edit(id: number) {
        const dialogData: CustomDimensionDialogData = {
            dimensionsData: this.dimensions,
            editItemId: id,
        };
        this.openPopup()(dialogData);
    }

    create() {
        const dialogData: CustomDimensionDialogData = {
            dimensionsData: this.dimensions,
        };
        this.openPopup()(dialogData);
    }

    openPopup() {
        return (dialogData: CustomDimensionDialogData) => {
            const dialogRef = this.dialog.open(CustomDimensionsComponent, {
                width: '950px',
                maxHeight: '610px',
                disableClose: true,
                data: dialogData,
            });
            dialogRef.afterClosed().subscribe(async (submit: CustomDimensionDialogSubmitData) => {
                if (submit) {
                    await this.init();
                    this.sendAnalytics(submit.mode);
                }
            });
        };
    }

    sendAnalytics(mode: string) {
        let eventId: number;
        switch (mode) {
            case 'Add':
                eventId = customDimensionEvents.Create;
                break;
            case 'Edit':
                eventId = customDimensionEvents.Edit;
                break;
            case 'Clone':
                eventId = customDimensionEvents.Clone;
                break;
            case 'Archive':
                eventId = customDimensionEvents.Archive;
                break;
            default:
                break;
        }
        if (!eventId) {
            return;
        }
        this.feature.trigger(featureCustomDimension, eventId, {});
    }

    search(keyword: string) {
        this.keyword = keyword;
        this.paginate();
    }

    async enable(id: number) {
        await this.client.customDimension.enable(id);
        this.allCustomDimensions.find((cm) => cm._id === id).status = this.CustomDimensionStatus.Active;
        this.paginate();
    }
}
